import React, { useMemo, useEffect } from 'react';
import { FiX, FiUser, FiClock, FiMapPin, FiSmartphone, FiMonitor, FiBriefcase, FiAlertCircle, FiCalendar } from 'react-icons/fi';
import { MapContainer, TileLayer, Marker, Tooltip, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import Modal from '../Modal';
import './style.css';

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function MapBounds({ stations, records }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    const bounds = L.latLngBounds();
    let hasPoints = false;

    if (stations && stations.length > 0) {
      stations.forEach(station => {
        if (station.lat && station.lng) {
          bounds.extend([station.lat, station.lng]);
          hasPoints = true;
        }
      });
    }

    records.forEach(record => {
      if (record.latitude && record.longitude) {
        bounds.extend([parseFloat(record.latitude), parseFloat(record.longitude)]);
        hasPoints = true;
      }
    });

    if (hasPoints && bounds.isValid()) {
      const timer = setTimeout(() => {
        if (map && map._container) {
          map.fitBounds(bounds, { padding: [50, 50], animate: false });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [map, stations, records]);

  return null;
}

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return Infinity;
  
  const R = 6371; // Radius of the earth in km
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2); 
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
  const d = R * c; // Distance in km
  return d;
};

export default function PointDetailsModal({ isOpen, onClose, pointData }) {
  const records = useMemo(() => {
    if (!pointData || !pointData.records) return [];
    return pointData.records;
  }, [pointData]);

  const journey = useMemo(() => {
    if (!pointData || !pointData.journey) return null;
    return pointData.journey;
  }, [pointData]);

  const uniqueStations = useMemo(() => {
    if (!records) return [];
    const stations = new Map();
    
    records.forEach(record => {
      if (record.station_latitude && record.station_longitude) {
        const key = `${record.station_latitude}_${record.station_longitude}`;
        if (!stations.has(key)) {
          stations.set(key, {
            lat: parseFloat(record.station_latitude),
            lng: parseFloat(record.station_longitude),
            name: record.station_name,
            radius: record.fence_radius_km ? record.fence_radius_km * 1000 : 0
          });
        }
      }
    });
    
    return Array.from(stations.values());
  }, [records]);

  const mapCenter = useMemo(() => {
    // Center on first station if available, else first record, else default
    if (uniqueStations.length > 0) {
        return [uniqueStations[0].lat, uniqueStations[0].lng];
    }
    if (records.length > 0) {
      const rec = records.find(r => r.latitude && r.longitude);
      if (rec) return [parseFloat(rec.latitude), parseFloat(rec.longitude)];
    }
    return [-15.7942, -47.8822]; // Default (Brasilia)
  }, [records, uniqueStations]);

  // Calculate worked hours vs predicted
  const hoursData = useMemo(() => {
    if (!records || records.length < 2) return { worked: '0h 0min', predicted: '8h 0min', percent: 0, status: 'N/A' };

    // Simple calculation logic for display (can be refined)
    // Assuming even records are Entry, odd are Exit.
    let totalMinutes = 0;
    for (let i = 0; i < records.length - 1; i += 2) {
      const start = records[i].time.split(':').map(Number);
      const end = records[i+1].time.split(':').map(Number);
      const startMins = start[0] * 60 + start[1];
      let endMins = end[0] * 60 + end[1];
      
      // Handle overnight shift (crossing midnight)
      if (endMins < startMins) {
        endMins += 24 * 60;
      }

      totalMinutes += (endMins - startMins);
    }

    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;

    const predictedMins = journey ? (journey.daily_hours * 60) : 480; // 8h default
    const percent = Math.min(100, (totalMinutes / predictedMins) * 100);

    return {
      worked: `${h}h ${m}min`,
      predicted: journey ? `${journey.daily_hours}h` : '8h',
      percent,
      status: totalMinutes < predictedMins ? 'Abaixo do Previsto' : 'Dentro do Previsto'
    };
  }, [records, journey]);

  const photoUrl = useMemo(() => {
    if (!pointData) return null;
    if (pointData.employee_photo) return pointData.employee_photo;
    if (records && records.length > 0) {
        // Try to find a photo in records, prefer the last one (most recent)
        for (let i = records.length - 1; i >= 0; i--) {
            if (records[i].photo) return records[i].photo;
        }
    }
    return null;
  }, [pointData, records]);

  if (!pointData) return null;

  const dateObj = new Date(pointData.competence_date + 'T12:00:00');
  const monthNames = ["JAN", "FEV", "MAR", "ABR", "MAI", "JUN", "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"];
  const month = monthNames[dateObj.getMonth()];
  const day = dateObj.getDate();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Detalhes do Ponto"
      size="lg"
    >
      <div className="point-details-modal-content">
        {/* Header Card */}
        <div className="pd-header-card">
          <div className="pd-user-photo-container">
             {photoUrl ? (
               <img src={photoUrl} alt={pointData.employee_name} className="pd-user-photo" />
             ) : (
               <div className="pd-user-photo-placeholder">
                 <FiUser size={32} />
               </div>
             )}
          </div>
          <div className="pd-employee-info">
            <h3 className="pd-employee-name">{pointData.employee_name}</h3>
            <div className="pd-meta-row">
              <div className="pd-meta-item">
                <FiBriefcase size={14} />
                {pointData.registration_number}
              </div>
              <div className="pd-meta-item">
                <FiUser size={14} />
                {pointData.position_name}
              </div>
              <div className="pd-meta-item">
                <FiClock size={14} />
                {records.length} Registros
              </div>
              <div className="pd-meta-item">
                <FiCalendar size={14} />
                {dateObj.toLocaleDateString('pt-BR')}
              </div>
            </div>
            <div className="pd-meta-row">
              <div className="pd-meta-item">
                <FiMapPin size={14} />
                {uniqueStations.map(s => s.name).join(', ')}
              </div>
            </div>
            <div className="pd-meta-row">
               <div className="pd-meta-item" style={{ color: '#3b82f6' }}>
                  {pointData.journey_name}
                  {pointData.journey && pointData.journey.start_time && ` - ${pointData.journey.start_time} às ${pointData.journey.end_time}`}
               </div>
            </div>

            {/* Progress Bar */}
            <div className="pd-progress-section">
              <div className="pd-progress-bar">
                <div 
                  className="pd-progress-fill" 
                  style={{ 
                    width: `${hoursData.percent}%`,
                    backgroundColor: hoursData.percent >= 100 ? '#16a34a' : '#3b82f6' 
                  }}
                />
              </div>
              <div className="pd-progress-labels">
                <span><FiClock size={12} style={{marginRight: 4}}/> {hoursData.worked} de {hoursData.predicted} previstas</span>
                <span style={{ color: hoursData.status.includes('Abaixo') ? '#dc2626' : '#16a34a' }}>
                  {hoursData.status.includes('Abaixo') ? '↓' : '↑'} {hoursData.status}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Timeline Section */}
        <div>
          <h4 className="pd-section-title" style={{marginTop: '0px'}}><FiUser /> Pontos Registrados</h4>
          <div className="pd-timeline">
            {records.map((rec, index) => (
              <div key={index} className="pd-timeline-card">
                 <img 
                    src={rec.photo ? rec.photo : 'https://via.placeholder.com/60'} 
                    alt="Foto" 
                    className="pd-timeline-photo"
                 />
                 <div className="pd-timeline-info">
                    <div className="pd-time-row">
                      <span className="pd-time">{rec.time}</span>
                      <span className="pd-status-badge">On-Line</span>
                    </div>
                    <span className="pd-location">{rec.station_name}</span>
                    <div className="pd-tags">
                      {rec.latitude ? (() => {
                         let isInside = false;
                         if (uniqueStations.length > 0) {
                             isInside = uniqueStations.some(station => {
                                 const dist = calculateDistance(
                                     parseFloat(rec.latitude), 
                                     parseFloat(rec.longitude), 
                                     station.lat, 
                                     station.lng
                                 );
                                 return dist <= (station.radius / 1000); // radius is in meters, convert to km
                             });
                         }
                         
                         // If no stations defined but has location, assume inside (or handle differently)
                         // But requirement says "No local" / "Fora do local" based on stations
                         // If no stations, maybe just show "Localização Capturada" or similar.
                         // Assuming stations are passed correctly.
                         
                         return isInside ? (
                             <div className="pd-tag success" title="Dentro do local de trabalho">
                                <FiMapPin size={10} /> No Local
                             </div>
                         ) : (
                             <div className="pd-tag alert" title="Fora do local de trabalho">
                                <FiAlertCircle size={10} /> Fora do Local
                             </div>
                         );
                      })() : (
                         <div className="pd-tag alert" title="Sem Localização">
                            <FiAlertCircle size={10} /> Sem Loc.
                         </div>
                      )}
                      <div className="pd-tag type">
                        {index % 2 === 0 ? 'Entrada' : 'Saída'}
                      </div>
                    </div>
                 </div>
              </div>
            ))}
          </div>
        </div>

        {/* Map Section */}
        <div>
           <div className="pd-map-container">
              <MapContainer 
                  center={mapCenter} 
                  zoom={15} 
                  style={{ height: '100%', width: '100%' }}
                  scrollWheelZoom={true}
              >
                 <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                 />
                 
                 <MapBounds stations={uniqueStations} records={records} />

                 {/* Stations Markers */}
                 {uniqueStations.map((station, idx) => (
                    <React.Fragment key={`station-${idx}`}>
                        <Marker position={[station.lat, station.lng]}>
                           <Tooltip direction="bottom" offset={[0, 10]} opacity={1} permanent>
                              {station.name}
                           </Tooltip>
                        </Marker>
                        {station.radius > 0 && (
                           <Circle 
                              center={[station.lat, station.lng]}
                              radius={station.radius}
                              pathOptions={{ color: 'var(--primary-color)', fillColor: 'var(--primary-color)', fillOpacity: 0.1 }} 
                           />
                        )}
                    </React.Fragment>
                 ))}

                 {/* Records Markers */}
                 {records.map((rec, idx) => (
                    (rec.latitude && rec.longitude) && (
                      <Marker 
                        key={idx} 
                        position={[parseFloat(rec.latitude), parseFloat(rec.longitude)]}
                      >
                           <Tooltip permanent direction="top" offset={[0, -10]}>
                              {rec.time}
                           </Tooltip>
                      </Marker>
                    )
                 ))}
              </MapContainer>
           </div>
        </div>

      </div>
    </Modal>
  );
}
